import gql from 'graphql-tag';
import { compose } from 'recompose';
import { get } from 'utils';
import { TYPES, OPERATORS } from 'consts/filters';
import { withMemo } from 'hoc/utils';
import { withRealtimeHighlights } from 'hoc/highlights';
import { formatMatchesByType, mapFiltersToRules, getBookmarkFilters } from 'utils/streams';
import { contentHighlightsStreamQuery } from 'graphql/bookmarks';
import { scheduledAudioCallFragment } from 'graphql/fragments/audioCalls';
import { bookmarkFragment, bookmarkTargetFragment } from 'graphql/fragments/bookmarks';
import { streamFragment } from 'graphql/fragments/streams';
import { graphql } from 'graphql/utils';

export const withData = () =>
    compose(
        withMemo({
            formatMatchesByType
        }),
        graphql(
            gql`
                query withUserHighlightsCount {
                    currentUser {
                        id
                        highlightsCount
                    }
                }
            `,
            {
                props: ({ data }) => ({ highlightsCount: get(data, 'currentUser.highlightsCount') }),
                options: {
                    fetchPolicy: 'cache-first'
                },
                skip: ({ highlightsCount }) => highlightsCount !== undefined
            }
        ),
        graphql(contentHighlightsStreamQuery, {
            props: ({ data, ownProps: { formatMatchesByType: formatMatches } }) => {
                const matches = formatMatches(get(data, 'streams[0].matches.results', []));
                return {
                    loading: (!matches || matches.length === 0) && get(data, 'loading'),
                    matches,
                    refreshHighlights: data.refetch,
                    fetchMore: data.fetchMore
                };
            },
            options: ({ eventId, filterKey, sortKey }) => {
                const filters = [
                    { type: TYPES.event, operator: OPERATORS.is, value: eventId },
                    ...getBookmarkFilters(filterKey)
                ];

                return {
                    fetchPolicy: 'cache-and-network',
                    returnPreviousData: true,
                    variables: {
                        streamTypes: ['bookmarks'],
                        rules: mapFiltersToRules(filters),
                        sort: sortKey === 'chron' ? 'transcript_timestamp' : undefined
                    }
                };
            }
        }),
        graphql(
            gql`
                mutation ShareBookmark($bookmarkId: ID!, $shared: Boolean!) {
                    shareBookmark(input: { bookmarkId: $bookmarkId, shared: $shared }) {
                        bookmark {
                            ...bookmark
                        }
                    }
                }
                ${bookmarkFragment}
            `,
            {
                props: ({ mutate, ownProps: { setStatusBanner } }) => ({
                    setVisibility: ({ bookmarkId, shared }) => {
                        return mutate({
                            variables: {
                                bookmarkId,
                                shared
                            }
                        })
                            .then(() => {
                                setStatusBanner('Highlight updated successfully!');
                            })
                            .catch(error => {
                                setStatusBanner(`Error updating highlight: ${error}`, 'error', 'circleX');
                                throw error;
                            });
                    }
                })
            }
        ),
        graphql(
            gql`
                mutation manageEventSharing(
                    $shareSetting: Boolean!
                    $eventId: ID!
                    $streamTypes: [StreamType]!
                    $rules: [StreamRuleInput]
                    $size: Int = 100
                    $offset: Int = 0
                    $sort: String
                ) {
                    updateUserObjectSettings(
                        input: { targetType: event, targetId: $eventId, shareBookmarks: $shareSetting }
                    ) {
                        success
                        settings {
                            id
                            shareBookmarks
                        }
                        query {
                            audioCalls: events(eventIds: [$eventId]) {
                                ...scheduledAudioCall
                                userSettings {
                                    id
                                    shareBookmarks
                                }
                            }
                            streams(filter: { previewStreamTypes: $streamTypes }) {
                                ...stream
                                matches(size: $size, fromIndex: $offset, sort: $sort, filter: { rules: $rules }) {
                                    total
                                    results {
                                        id
                                        bookmark {
                                            ...bookmark
                                            ...bookmarkTarget
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                ${scheduledAudioCallFragment}
                ${streamFragment}
                ${bookmarkFragment}
                ${bookmarkTargetFragment}
            `,
            {
                props: props => {
                    const { mutate, ownProps } = props;
                    const { setStatusBanner, eventId, filterKey, sortKey } = ownProps;
                    const filters = [
                        { type: TYPES.event, operator: OPERATORS.is, value: eventId },
                        ...getBookmarkFilters(filterKey)
                    ];
                    return {
                        manageShareSettings: shareSetting =>
                            mutate({
                                variables: {
                                    eventId,
                                    shareSetting,
                                    streamTypes: ['bookmarks'],
                                    rules: mapFiltersToRules(filters),
                                    sort: sortKey === 'chron' ? 'transcript_timestamp' : undefined,
                                    size: 100,
                                    offset: 0
                                }
                            })
                                .then(() => {
                                    setStatusBanner('Event settings changed successfully!');
                                })
                                .catch(error => {
                                    setStatusBanner(`Error changing settings: ${error}`, 'error', 'circleX');
                                })
                    };
                }
            }
        ),
        withRealtimeHighlights()
    );
